import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import adminService from '../../services/admin.service';
import type {
  AnalysisConfigurationResponse,
  AnalysisRuleSet,
  CatalogRule,
  RuleCatalogResponse,
  RuleOverride,
} from '../../types/analysis-config.types';
import type { CustomRuleResponse } from '../../types/custom-rule.types';
import type { RuleCategoryKey, RuleSeverity } from '../../types/policy.types';
import CategoryBadge from '../shared/CategoryBadge';
import RegulationCitation from '../shared/RegulationCitation';
import CustomRuleEditorModal from './CustomRuleEditorModal';

interface EvaluationCriteriaEditorProps {
  userId: string;
}

const CATEGORY_ORDER: RuleCategoryKey[] = ['compliance', 'legal_risk', 'qa'];
const SEVERITY_OPTIONS: RuleSeverity[] = ['low', 'medium', 'high', 'critical'];

function emptyRuleSet(): AnalysisRuleSet {
  return {
    identity_verification_window_seconds: 15,
    purpose_declaration_interaction_limit: 4,
    greeting_timeout_seconds: 10,
    empathy_frequency_minutes: 3,
    interruption_threshold: 5,
    sentiment_drop_threshold: 2,
    sentiment_window_seconds: 30,
    mandatory_disclosures: [],
    prohibited_phrases: [],
    rules: {},
  };
}

function effectiveValueFor(
  ruleset: AnalysisRuleSet,
  rule: CatalogRule,
  paramName: string,
): unknown {
  const override = ruleset.rules?.[rule.slug];
  if (override?.params && paramName in override.params) {
    return override.params[paramName];
  }
  const legacy = ruleset as unknown as Record<string, unknown>;
  if (paramName in legacy) return legacy[paramName];
  const cat = rule.tunable_params.find((p) => p.name === paramName);
  return cat?.default ?? null;
}

function ensureOverride(
  ruleset: AnalysisRuleSet,
  slug: string,
): { ruleset: AnalysisRuleSet; override: RuleOverride } {
  const next: AnalysisRuleSet = {
    ...ruleset,
    rules: { ...(ruleset.rules ?? {}) },
  };
  const existing = next.rules![slug] ?? {};
  const override: RuleOverride = {
    enabled: existing.enabled ?? true,
    severity: existing.severity ?? null,
    params: { ...(existing.params ?? {}) },
  };
  next.rules![slug] = override;
  return { ruleset: next, override };
}

export default function EvaluationCriteriaEditor({ userId }: EvaluationCriteriaEditorProps) {
  const { t } = useTranslation('admin');
  const [catalog, setCatalog] = useState<RuleCatalogResponse | null>(null);
  const [config, setConfig] = useState<AnalysisConfigurationResponse | null>(null);
  const [draft, setDraft] = useState<AnalysisRuleSet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState<RuleCategoryKey>('compliance');
  const [customRules, setCustomRules] = useState<CustomRuleResponse[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<CustomRuleResponse | null>(null);

  const reloadCustomRules = useCallback(async () => {
    try {
      const res = await adminService.listCustomRules(userId);
      setCustomRules(res.rules);
    } catch {
      // best-effort — surfaces in the empty state
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      adminService.getRuleCatalog(),
      adminService.getDefaultAnalysisConfig(userId),
      adminService.listCustomRules(userId),
    ])
      .then(([cat, cfg, custom]) => {
        if (cancelled) return;
        setCatalog(cat);
        setConfig(cfg);
        setDraft({
          ...emptyRuleSet(),
          ...cfg.config,
          rules: { ...(cfg.config.rules ?? {}) },
        });
        setCustomRules(custom.rules);
      })
      .catch(() => {
        if (!cancelled) setError(
          t('criteria.errors.load', { defaultValue: 'Failed to load evaluation criteria.' }),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, t]);

  const isDirty = useMemo(() => {
    if (!config || !draft) return false;
    return JSON.stringify(config.config) !== JSON.stringify(draft);
  }, [config, draft]);

  function handleToggle(slug: string, enabled: boolean) {
    if (!draft) return;
    const { ruleset, override } = ensureOverride(draft, slug);
    override.enabled = enabled;
    setDraft({ ...ruleset, rules: { ...ruleset.rules!, [slug]: { ...override } } });
  }

  function handleSeverity(slug: string, severity: RuleSeverity | '') {
    if (!draft) return;
    const { ruleset, override } = ensureOverride(draft, slug);
    override.severity = severity === '' ? null : severity;
    setDraft({ ...ruleset, rules: { ...ruleset.rules!, [slug]: { ...override } } });
  }

  function handleParamChange(slug: string, paramName: string, value: unknown) {
    if (!draft) return;
    const { ruleset, override } = ensureOverride(draft, slug);
    override.params = { ...(override.params ?? {}), [paramName]: value as never };
    // Also update legacy top-level field if it exists, so the legacy
    // analyzer paths still see the new value when they don't read `rules`.
    const next = { ...ruleset, rules: { ...ruleset.rules!, [slug]: { ...override } } };
    if (paramName in next) {
      (next as unknown as Record<string, unknown>)[paramName] = value;
    }
    setDraft(next);
  }

  async function handleSave() {
    if (!draft || !config) return;
    setSaving(true);
    try {
      if (config.id) {
        await adminService.updateAnalysisConfig(userId, config.id, {
          config: draft,
        });
      } else {
        // No persisted default yet — create one.
        await adminService.createAnalysisConfig(userId, {
          name: config.name || 'Default Configuration',
          description: config.description ?? null,
          is_default: true,
          config: draft,
        });
      }
      toast.success(
        t('criteria.saved', { defaultValue: 'Evaluation criteria saved.' }),
      );
      // Reload to pick up new id when first save was a create.
      const cfg = await adminService.getDefaultAnalysisConfig(userId);
      setConfig(cfg);
      setDraft({
        ...emptyRuleSet(),
        ...cfg.config,
        rules: { ...(cfg.config.rules ?? {}) },
      });
    } catch {
      toast.error(t('criteria.errors.save', { defaultValue: 'Failed to save criteria.' }));
    } finally {
      setSaving(false);
    }
  }

  function handleDiscard() {
    if (!config) return;
    setDraft({
      ...emptyRuleSet(),
      ...config.config,
      rules: { ...(config.config.rules ?? {}) },
    });
  }

  const rulesByCategory = useMemo(() => {
    const m = new Map<RuleCategoryKey, CatalogRule[]>();
    if (!catalog) return m;
    for (const r of catalog.rules) {
      const list = m.get(r.category) ?? [];
      list.push(r);
      m.set(r.category, list);
    }
    return m;
  }, [catalog]);

  if (loading) {
    return (
      <section className="rounded-xl bg-white p-6 shadow">
        <div className="h-5 w-56 animate-pulse rounded bg-gray-200" />
      </section>
    );
  }
  if (error) {
    return (
      <section className="rounded-xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
        {error}
      </section>
    );
  }
  if (!catalog || !draft || !config) return null;

  return (
    <section className="rounded-xl bg-white shadow">
      <header className="flex flex-col gap-3 border-b border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t('criteria.title', { defaultValue: 'Evaluation Criteria' })}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('criteria.subtitle', {
              defaultValue:
                "Toggle rules, change severity, and tune thresholds. Changes apply to this customer's future analyses.",
            })}
          </p>
          <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-indigo-50 px-2 py-1 text-xs text-indigo-700">
            <span className="font-medium">{config.name}</span>
            {config.is_default && (
              <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                {t('criteria.default', { defaultValue: 'Default' })}
              </span>
            )}
            <span className="text-indigo-500">· catalog v{catalog.catalog_version}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t('criteria.unsaved', { defaultValue: 'Unsaved changes' })}
            </span>
          )}
          <button
            type="button"
            onClick={handleDiscard}
            disabled={!isDirty || saving}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('criteria.discard', { defaultValue: 'Discard' })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
            {t('criteria.save', { defaultValue: 'Save as new version' })}
          </button>
        </div>
      </header>

      {/* Category tabs */}
      <nav className="border-b border-gray-200 px-5">
        <div className="-mb-px flex flex-wrap gap-4">
          {CATEGORY_ORDER.map((c) => {
            const count = rulesByCategory.get(c)?.length ?? 0;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCategory(c)}
                className={`flex items-center gap-1.5 border-b-2 pb-3 pt-2 text-sm font-medium transition ${
                  activeCategory === c
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <CategoryBadge category={c} size="xs" />
                <span className="text-xs text-gray-400">({count})</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="space-y-3 p-5">
        {/* Custom rules in this category */}
        {customRules
          .filter((r) => r.category === activeCategory)
          .map((cr) => (
            <CustomRuleRow
              key={cr.id}
              rule={cr}
              onEdit={() => {
                setEditingRule(cr);
                setModalOpen(true);
              }}
              onDelete={async () => {
                if (!confirm(`Delete "${cr.name}"? This cannot be undone.`)) return;
                try {
                  await adminService.deleteCustomRule(userId, cr.id);
                  toast.success('Rule deleted.');
                  await reloadCustomRules();
                } catch {
                  toast.error('Failed to delete rule.');
                }
              }}
              onToggle={async (enabled) => {
                try {
                  await adminService.updateCustomRule(userId, cr.id, { enabled });
                  await reloadCustomRules();
                } catch {
                  toast.error('Failed to update rule.');
                }
              }}
            />
          ))}

        {/* "+ Add custom rule" button */}
        <button
          type="button"
          onClick={() => {
            setEditingRule(null);
            setModalOpen(true);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 p-3 text-sm font-medium text-gray-600 transition hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add custom rule for this category
        </button>

        {/* Catalog rules */}
        {(rulesByCategory.get(activeCategory) ?? []).map((rule) => {
          const override = draft.rules?.[rule.slug];
          const enabled = override?.enabled ?? true;
          const severity = (override?.severity ?? rule.severity_default) as RuleSeverity;
          const isOverridden = !!override && (
            (override.enabled !== undefined && override.enabled !== true) ||
            (override.severity ?? null) !== null ||
            Object.keys(override.params ?? {}).length > 0
          );
          return (
            <div
              key={rule.slug}
              className={`rounded-lg border p-4 ${enabled ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-75'}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">{rule.name}</h4>
                    {isOverridden && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                        {t('criteria.overridden', { defaultValue: 'Customized' })}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-600">{rule.description_short}</p>
                  {rule.regulation_refs.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {rule.regulation_refs.map((reg) => (
                        <RegulationCitation
                          key={`${rule.slug}-${reg.jurisdiction}-${reg.law}`}
                          regulation={reg}
                          size="xs"
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => handleToggle(rule.slug, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>{enabled ? t('criteria.enabled', { defaultValue: 'Enabled' }) : t('criteria.disabled', { defaultValue: 'Disabled' })}</span>
                  </label>
                  <select
                    value={severity}
                    onChange={(e) => handleSeverity(rule.slug, e.target.value as RuleSeverity)}
                    className="rounded-md border-gray-300 text-xs"
                    disabled={!enabled}
                  >
                    {SEVERITY_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {t(`criteria.severity.${s}`, { defaultValue: s })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {rule.tunable_params.length > 0 && (
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {rule.tunable_params.map((param) => {
                    const value = effectiveValueFor(draft, rule, param.name);
                    if (param.type === 'string_list') {
                      const lines = Array.isArray(value)
                        ? (value as string[]).join('\n')
                        : '';
                      return (
                        <label key={param.name} className="block">
                          <span className="block text-xs font-medium text-gray-700">
                            {param.label}
                          </span>
                          <textarea
                            value={lines}
                            onChange={(e) =>
                              handleParamChange(
                                rule.slug,
                                param.name,
                                e.target.value
                                  .split('\n')
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              )
                            }
                            disabled={!enabled}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 text-xs shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                            placeholder={param.description}
                          />
                        </label>
                      );
                    }
                    const isInt = param.type === 'int';
                    return (
                      <label key={param.name} className="block">
                        <span className="block text-xs font-medium text-gray-700">
                          {param.label}
                          {param.unit ? <span className="ml-1 text-gray-400">({param.unit})</span> : null}
                        </span>
                        <input
                          type="number"
                          value={typeof value === 'number' ? value : Number(value ?? param.default ?? 0)}
                          min={param.min_value ?? undefined}
                          max={param.max_value ?? undefined}
                          step={isInt ? 1 : 'any'}
                          disabled={!enabled}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = isInt ? parseInt(raw, 10) : parseFloat(raw);
                            if (Number.isNaN(parsed)) return;
                            handleParamChange(rule.slug, param.name, parsed);
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 text-xs shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                        <span className="text-[10px] text-gray-500">{param.description}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CustomRuleEditorModal
        open={modalOpen}
        userId={userId}
        initial={editingRule}
        onClose={() => setModalOpen(false)}
        onSaved={async () => {
          await reloadCustomRules();
        }}
      />
    </section>
  );
}

interface CustomRuleRowProps {
  rule: CustomRuleResponse;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (enabled: boolean) => void;
}

function CustomRuleRow({ rule, onEdit, onDelete, onToggle }: CustomRuleRowProps) {
  const enabled = rule.enabled;
  const cfg = rule.config;
  const phraseCount =
    cfg.rule_type === 'phrase_required' || cfg.rule_type === 'phrase_forbidden'
      ? cfg.phrases.length
      : 0;
  const summary =
    cfg.rule_type === 'phrase_required'
      ? `Required: ${phraseCount} phrase${phraseCount === 1 ? '' : 's'}` +
        (cfg.must_appear_within_seconds ? ` within ${cfg.must_appear_within_seconds}s` : '')
      : cfg.rule_type === 'phrase_forbidden'
      ? `Forbidden: ${phraseCount} phrase${phraseCount === 1 ? '' : 's'}` +
        (cfg.pattern_type === 'regex' ? ' (regex)' : '')
      : cfg.rule_type;

  return (
    <div
      className={`rounded-lg border-l-4 border-l-indigo-400 border border-gray-200 p-4 ${
        enabled ? 'bg-white' : 'bg-gray-50 opacity-75'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{rule.name}</h4>
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-800 ring-1 ring-inset ring-indigo-200">
              Custom
            </span>
            <span
              title={`rule_version ${rule.rule_version}`}
              className="font-mono text-[10px] text-gray-500"
            >
              v{rule.rule_version.slice(0, 6)}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-600">{summary}</p>
          {rule.regulation_refs.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {rule.regulation_refs.map((reg) => (
                <RegulationCitation
                  key={`${rule.id}-${reg.jurisdiction}-${reg.law}`}
                  regulation={reg}
                  size="xs"
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => onToggle(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>{enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title="Edit"
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
